import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { EInvoiceChip } from '@/components/einvoicing/einvoice-chip';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/lib/api';
import { useMe } from '@/lib/clients';
import {
  FILTERS,
  canResubmit,
  formatDateTime,
  matchesFilter,
  useEInvoices,
  useEInvoicingSettings,
  useResubmit,
  type EInvoiceFilter,
  type EInvoiceRow,
} from '@/lib/einvoicing';
import { formatAmount, formatDate } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';

const KEYS = FILTERS.map(([k]) => k);

export const Route = createFileRoute('/taxes/e-invoicing')({
  component: EInvoicing,
  validateSearch: (s: Record<string, unknown>): { status?: EInvoiceFilter } =>
    KEYS.includes(s['status'] as EInvoiceFilter) ? { status: s['status'] as EInvoiceFilter } : {},
});

const docPath = (r: EInvoiceRow) =>
  r.kind === 'credit_note'
    ? `/sales/credit-notes/${r.documentId}`
    : `/sales/invoices/${r.documentId}`;

/**
 * E-invoicing exceptions (document 06 "E-invoicing"): what the service provider or local
 * validation rejected and deliveries that used up their retries, with the reason and Resubmit;
 * chips for the other states. Only reachable while e-invoicing is on for the company.
 */
function EInvoicing() {
  const orgId = useOrgId();
  const settings = useEInvoicingSettings();
  const rows = useEInvoices();
  const navigate = useNavigate();
  const { status = 'exceptions' } = Route.useSearch() as { status?: EInvoiceFilter };
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const all = rows.data ?? [];
  const shown = all.filter(
    (r) =>
      matchesFilter(r, status) &&
      (!needle ||
        r.number.toLowerCase().includes(needle) ||
        r.contactName.toLowerCase().includes(needle) ||
        (r.aspReference ?? '').toLowerCase().includes(needle)),
  );
  const count = (f: EInvoiceFilter) => all.filter((r) => matchesFilter(r, f)).length;

  return (
    <>
      <PageHeader
        title="E-invoicing"
        description="Invoices and credit notes sent through the service provider, and what needs fixing."
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : settings.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !settings.data?.enabled ? (
        <EmptyState
          title="E-invoicing is off for this company"
          hint="Your accountant switches it on in Company settings, on the Sales tab."
          action={
            <Button variant="outline" asChild>
              <Link to="/settings/company" search={{ tab: 'sales' }}>
                Company settings
              </Link>
            </Button>
          }
        />
      ) : (
        <>
          <div
            className="mb-3 flex flex-wrap items-center gap-2"
            role="tablist"
            aria-label="Status"
          >
            {FILTERS.map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={status === k}
                onClick={() =>
                  void navigate({
                    to: '/taxes/e-invoicing',
                    search: k === 'exceptions' ? {} : { status: k },
                  })
                }
                className={cn(
                  'rounded-full border px-3 py-1 text-sm',
                  status === k
                    ? 'border-primary bg-accent font-medium text-accent-foreground'
                    : 'bg-card text-muted-foreground hover:bg-muted',
                )}
              >
                {label}
                {rows.data ? <span className="ml-1 tnum">({count(k)})</span> : null}
              </button>
            ))}
          </div>
          <div className="mb-3 relative w-[340px]">
            <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-muted-foreground" />
            <Input
              className="bg-card pl-8"
              placeholder="Search by number, customer or reference"
              aria-label="Search e-invoices"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          {rows.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : rows.isError ? (
            <EmptyState title="Could not load e-invoices" hint={rows.error.message} />
          ) : shown.length === 0 ? (
            <EmptyState
              title={
                status === 'exceptions' ? 'No exceptions' : 'No e-invoices match these filters'
              }
              hint={
                status === 'exceptions'
                  ? 'Every e-invoice was accepted or is on its way.'
                  : undefined
              }
            />
          ) : (
            <ExceptionsTable rows={shown} onOpen={(r) => void navigate({ to: docPath(r) })} />
          )}
        </>
      )}
    </>
  );
}

function ExceptionsTable({
  rows,
  onOpen,
}: {
  rows: EInvoiceRow[];
  onOpen: (r: EInvoiceRow) => void;
}) {
  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader className="sticky top-0 bg-card">
          <TableRow>
            <TableHead className="w-[100px]">Date</TableHead>
            <TableHead className="w-[120px]">No.</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead className="w-[120px] text-right">Total</TableHead>
            <TableHead className="w-[140px]">E-invoice</TableHead>
            <TableHead className="min-w-[220px]">Details</TableHead>
            <TableHead className="w-[130px]">Last update</TableHead>
            <TableHead className="w-[130px] text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow
              key={r.submissionId}
              className="cursor-pointer"
              onClick={() => onOpen(r)}
              data-testid="einvoice-row"
            >
              <TableCell>{formatDate(r.issueDate)}</TableCell>
              <TableCell
                className="tnum"
                title={r.kind === 'credit_note' ? 'Credit note' : 'Invoice'}
              >
                {r.number}
              </TableCell>
              <TableCell className="font-medium whitespace-normal">{r.contactName}</TableCell>
              <TableCell className="tnum text-right">
                {r.kind === 'credit_note' ? '-' : ''}
                {formatAmount(r.total)}
              </TableCell>
              <TableCell>
                <EInvoiceChip status={r.status} needsAttention={r.needsAttention} />
              </TableCell>
              <TableCell className="text-sm whitespace-normal text-muted-foreground">
                <span className="line-clamp-2">{reason(r)}</span>
              </TableCell>
              <TableCell className="text-sm whitespace-normal tnum">
                {formatDateTime(r.updatedAt)}
              </TableCell>
              <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                <RowAction r={r} onOpen={() => onOpen(r)} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function reason(r: EInvoiceRow): string {
  if (r.errors.length) return r.errors.map((e) => e.message).join('; ');
  if (r.needsAttention)
    return `Delivery failed ${r.attempts} times: ${r.lastError ?? 'service provider unavailable'}`;
  if (r.status === 'pending' && r.lastError) return `Retrying: ${r.lastError}`;
  if (r.status === 'not_required') return r.statusReason ?? '';
  if (r.aspReference) return `Reference ${r.aspReference}`;
  return '';
}

function RowAction({ r, onOpen }: { r: EInvoiceRow; onOpen: () => void }) {
  const me = useMe();
  const resubmit = useResubmit();
  if (me.data?.isStaff && canResubmit(r))
    return (
      <Button
        size="sm"
        variant="link"
        className="px-2"
        disabled={resubmit.isPending}
        onClick={() =>
          resubmit.mutate(r.documentId, {
            onSuccess: () =>
              toast(`${r.number} ${r.status === 'rejected' ? 'resubmitted' : 'queued again'}.`),
            onError: (e) => toast(e instanceof ApiError ? e.message : String(e), { tone: 'error' }),
          })
        }
      >
        {r.status === 'rejected' ? 'Resubmit' : 'Retry delivery'}
      </Button>
    );
  return (
    <Button size="sm" variant="link" className="px-2" onClick={onOpen}>
      View
    </Button>
  );
}
